import {List, ListItem, ListItemOpts, ListOpts} from '../../../ui/list';
import {Obj, OBJ, SIGNAL, SLOT} from '../../../obj';
import {ToolButton, ToolButtonOpts} from '../../../ui/toolbutton';
import {CheckState} from '../../../constants';
import {ElObj, elObjOpts} from '../../../elobj';
import {getLogger} from '../../../logging';
import {bind, isNumber, stringIterableToStringArray} from '../../../util';
import {Point} from '../../../tools';

const logger = getLogger('projectdetailview');

@OBJ
export class FilterList extends List {
	private selectedFilterId: number | null;

	constructor(opts: Partial<ListOpts> | null, tagName: TagName, parent?: ElObj | null);
	constructor(opts: Partial<ListOpts> | null, root: Element | null, parent?: ElObj | null);
	constructor(tagName: TagName, parent?: ElObj | null);
	constructor(root: Element | null, parent?: ElObj | null);
	constructor(opts: Partial<ListOpts> | null, tagName?: TagName);
	constructor(opts: Partial<ListOpts> | null, root?: Element | null);
	constructor(opts: Partial<ListOpts>, parent?: ElObj | null);
	constructor(opts?: Partial<ListOpts>);
	constructor(root?: Element | null);
	constructor(tagName?: TagName);
	constructor(parent?: ElObj | null);
	constructor(a?: Partial<ListOpts> | ElObj | Element | TagName | null, b?: ElObj | Element | TagName | null, c?: ElObj | null) {
		const opts = elObjOpts<ListOpts>(a, b, c);
		super(opts);
		this.selectedFilterId = null;
	}

	addFilter(filter: IFilter): void {
		if (this.indexForFilterId(filter.id) >= 0) {
			logger.info('addFilter: Filter already added.');
			return;
		}
		const item = new FilterListItem(this, filter);
		item.setSelected(filter.id === this.selectedFilterId);
		this.addItem(item);
	}

	activeFilterCount(): number {
		let count = 0;
		for (const obj of this.items) {
			const item = <FilterListItem>obj;
			if (item.isFilterEnabled()) {
				++count;
			}
		}
		return count;
	}

	@SIGNAL
	filterClicked(filterId: number, point: Point): void {
	}

	@SIGNAL
	filterScopeClicked(filterId: number): void {
	}

	@SIGNAL
	filterEnabledClicked(filterId: number): void {
	}

	@SIGNAL
	filterLabelRenameCommitted(filterId: number, label: string): void {
	}

	indexForFilterId(filterId: number): number {
		for (let i = 0; i < this.items.size(); ++i) {
			const item = <FilterListItem>this.items.at(i);
			if (item.filterId === filterId) {
				return i;
			}
		}
		return -1;
	}

	// Lane E PR 1e-ii — the IDs of every chip currently in the strip. Used by the
	// two-zone editor's full-tree reload to prune chips whose top-level filters were
	// replaced by the IR save.
	filterIds(): Array<number> {
		const ids: Array<number> = [];
		for (let i = 0; i < this.items.size(); ++i) {
			ids.push((<FilterListItem>this.items.at(i)).filterId);
		}
		return ids;
	}

	removeFilter(filterId: number): void {
		const index = this.indexForFilterId(filterId);
		if (index >= 0) {
			this.removeItem(index);
		} else {
			logger.error('removeFilter: Invalid index for object ID');
		}
	}

	updateFilter(filter: IFilter): void {
		for (const obj of this.items) {
			if ((<FilterListItem>obj).filterId === filter.id) {
				(<FilterListItem>obj).setFilter(filter);
				(<FilterListItem>obj).setSelected(filter.id === this.selectedFilterId);
				return;
			}
		}
		logger.error('updateFilter: List item was not found for given object');
	}

	setSelectedFilterId(filterId: number | null): void {
		this.selectedFilterId = filterId;
		for (const obj of this.items) {
			const item = <FilterListItem>obj;
			item.setSelected(item.filterId === this.selectedFilterId);
		}
	}
}

@OBJ
class FilterListItem extends ListItem {
	private countEl: ElObj | null;
	private enabledToggle: FilterEnabledToggle | null;
	private filterLabelText: string;
	filterId: number;
	private parentList: FilterList | null;
	private renamingInputEl: HTMLInputElement | null;
	private scopeCanFlyTo: boolean;

	constructor(parentList: FilterList | null, filter: IFilter, opts?: Partial<ListItemOpts>) {
		opts = opts || {};
		const filterLabel = (filter.label.trim().length > 0) ?
			filter.label :
			'Unlabeled Filter';
		opts.text = filterChipLabel(filterLabel);
		super(opts);
		this.countEl = null;
		this.enabledToggle = null;
		this.filterLabelText = filterLabel;
		this.filterId = filter.id;
		this.parentList = parentList;
		this.renamingInputEl = null;
		this.scopeCanFlyTo = false;
		this.addClass('lb-project-filter-chip', 'chip');
		this.textEl.addClass('chip__label');
		this.textEl.setAttribute('title', 'Double-click to rename');
		const labelEl = this.textEl.element();
		if (labelEl) {
			labelEl.addEventListener('dblclick', this.labelDoubleClickEvent);
		}
		this.setFilterKind(filter);
		this.initCountPill(filter);
		this.initEnabledToggle(filter.enabled);
		this.setFilterModeClasses(filter);
	}

	@SLOT
	private _clicked(point: Point): void {
		if (this.parentList) {
			this.parentList.filterClicked(this.filterId, point);
		}
	}

	private connectEnabledToggle(): void {
		if (this.enabledToggle) {
			Obj.connect(
				this, 'clicked',
				this, '_clicked');
			Obj.connect(
				this.enabledToggle, 'clicked',
				this, 'enabledToggleClicked');
		}
	}

	destroy(): void {
		const labelEl = this.textEl.element();
		if (labelEl) {
			labelEl.removeEventListener('dblclick', this.labelDoubleClickEvent);
		}
		this.finishInlineRename(false);
		if (this.countEl) {
			this.countEl.destroy();
		}
		this.disconnectScopeIcon();
		this.countEl = null;
		this.enabledToggle = null;
		this.filterLabelText = '';
		this.filterId = Number.NaN;
		this.parentList = null;
		super.destroy();
	}

	private beginInlineRename(): void {
		if (this.renamingInputEl) {
			this.renamingInputEl.focus();
			this.renamingInputEl.select();
			return;
		}
		const labelEl = this.textEl.element();
		if (!labelEl) {
			return;
		}
		labelEl.textContent = '';
		const input = document.createElement('input');
		input.className = 'chip__label chip__label-input';
		input.value = this.filterLabelText;
		input.setAttribute('aria-label', 'Filter label');
		input.addEventListener('blur', this.renameInputBlurEvent);
		input.addEventListener('click', this.renameInputBubbleStopEvent);
		input.addEventListener('dblclick', this.renameInputBubbleStopEvent);
		input.addEventListener('keydown', this.renameInputKeyDownEvent);
		labelEl.appendChild(input);
		this.renamingInputEl = input;
		window.setTimeout(() => {
			if (this.renamingInputEl) {
				this.renamingInputEl.focus();
				this.renamingInputEl.select();
			}
		}, 0);
	}

	private destroyEnabledToggle(): void {
		if (this.enabledToggle) {
			this.disconnectEnabledToggle();
			this.enabledToggle.destroy();
		}
	}

	private disconnectEnabledToggle(): void {
		if (this.enabledToggle) {
			Obj.disconnect(
				this, 'clicked',
				this, '_clicked');
			Obj.disconnect(
				this.enabledToggle, 'clicked',
				this, 'enabledToggleClicked');
		}
	}

	private disconnectScopeIcon(): void {
		if (this.leadingEl) {
			this.leadingEl.removeEventListener('click', this.scopeIconClickEvent);
		}
	}

	@SLOT
	private enabledToggleClicked(): void {
		if (this.parentList) {
			this.parentList.filterEnabledClicked(this.filterId);
		}
	}

	private finishInlineRename(commit: boolean): void {
		if (!this.renamingInputEl) {
			return;
		}
		const input = this.renamingInputEl;
		const next = input.value.trim();
		input.removeEventListener('blur', this.renameInputBlurEvent);
		input.removeEventListener('click', this.renameInputBubbleStopEvent);
		input.removeEventListener('dblclick', this.renameInputBubbleStopEvent);
		input.removeEventListener('keydown', this.renameInputKeyDownEvent);
		this.renamingInputEl = null;
		input.remove();
		this.setText(filterChipLabel(this.filterLabelText));
		if (commit && next && (next !== this.filterLabelText) && this.parentList) {
			this.parentList.filterLabelRenameCommitted(this.filterId, next);
		}
	}

	isFilterEnabled(): boolean {
		return Boolean(this.enabledToggle && (this.enabledToggle.checkState() === CheckState.Checked));
	}

	private initEnabledToggle(enabled: boolean = false): void {
		if (!this.enabledToggle) {
			this.enabledToggle = new FilterEnabledToggle(enabled);
			this.setTrailingObj(this.enabledToggle);
			this.enabledToggle.setDisabled(this.isDisabled());
			this.connectEnabledToggle();
		}
	}

	private initCountPill(filter: IFilter): void {
		if (this.countEl) {
			return;
		}
		this.countEl = new ElObj({
			classNames: 'chip__count',
			tagName: 'span',
		});
		this.updateCountPill(filter);
		if (this.trailingEl) {
			const element = this.element();
			const trailing = this.trailingEl.element();
			const countEl = this.countEl.element();
			if (element && trailing && countEl) {
				element.insertBefore(countEl, trailing);
				return;
			}
		}
		this.appendChild(this.countEl);
	}

	@bind
	private labelDoubleClickEvent(event: Event): void {
		event.preventDefault();
		event.stopPropagation();
		this.beginInlineRename();
	}

	@bind
	private renameInputBlurEvent(): void {
		this.finishInlineRename(true);
	}

	@bind
	private renameInputBubbleStopEvent(event: Event): void {
		event.stopPropagation();
	}

	@bind
	private renameInputKeyDownEvent(event: KeyboardEvent): void {
		event.stopPropagation();
		switch (event.key) {
			case 'Enter':
				event.preventDefault();
				this.finishInlineRename(true);
				break;
			case 'Escape':
				event.preventDefault();
				this.finishInlineRename(false);
				break;
		}
	}

	@bind
	private scopeIconClickEvent(event: MouseEvent): void {
		event.preventDefault();
		event.stopImmediatePropagation();
		event.stopPropagation();
		if (this.parentList && this.scopeCanFlyTo && !this.isDisabled()) {
			this.parentList.filterScopeClicked(this.filterId);
		}
	}

	setDisabled(disabled: boolean): void {
		if (disabled === this.isDisabled()) {
			return;
		}
		super.setDisabled(disabled);
		disabled = this.isDisabled();
		if (disabled) {
			this.disconnectEnabledToggle();
		}
		if (this.enabledToggle) {
			this.enabledToggle.setDisabled(disabled);
		}
		if (!disabled) {
			this.connectEnabledToggle();
		}
	}

	setFilter(filter: IFilter): void {
		this.filterId = filter.id;
		this.filterLabelText = (filter.label.trim().length > 0) ? filter.label : 'Unlabeled Filter';
		if (!this.renamingInputEl) {
			this.setText(filterChipLabel(this.filterLabelText));
		}
		this.setFilterKind(filter);
		this.updateCountPill(filter);
		this.setFilterModeClasses(filter);
		if (this.enabledToggle) {
			this.enabledToggle.setChecked(filter.enabled);
		}
	}

	private setFilterModeClasses(filter: IFilter): void {
		const enabled = filter.enabled;
		const displayCount = this.filterDisplayCount(filter);
		const emptyWarn = enabled && isNumber(displayCount) && (displayCount === 0);
		this.setClass(enabled, 'lb-project-filter-chip--active');
		this.setClass(!enabled, 'lb-project-filter-chip--paused');
		this.setClass(enabled, 'chip--enabled');
		this.setClass(!enabled, 'chip--paused');
		this.setClass(emptyWarn, 'chip--empty-warn');
	}

	setSelected(selected: boolean): void {
		this.setClass(selected, 'chip--selected');
	}

	private setFilterKind(filter: IFilter): void {
		const icon = filterChipScopeName(filter);
		this.disconnectScopeIcon();
		this.setAttribute('data-filter-kind', icon);
		this.setLeadingObj(filterChipScopeIcon(icon));
		if (this.leadingEl) {
			this.leadingEl.addClass('chip__scope');
			this.scopeCanFlyTo = filterChipScopeCanFlyTo(filter, icon);
			this.leadingEl.setClass(this.scopeCanFlyTo, 'chip__scope--fly-to');
			this.leadingEl.setAttribute('title', this.scopeCanFlyTo ? 'Fly to this filter' : '');
			this.leadingEl.setAttribute('aria-label', this.scopeCanFlyTo ? 'Fly to this filter' : '');
			if (this.scopeCanFlyTo) {
				this.leadingEl.addEventListener('click', this.scopeIconClickEvent);
			}
		} else {
			this.scopeCanFlyTo = false;
		}
	}

	private updateCountPill(filter: IFilter): void {
		if (!this.countEl) {
			return;
		}
		const count = this.filterDisplayCount(filter);
		this.countEl.setClass(!isNumber(count), 'is-empty');
		this.countEl.setText(isNumber(count) ? count.toLocaleString() : '');
	}

	private filterDisplayCount(filter: IFilter): number | null {
		const metrics = filter.metrics || null;
		if (!metrics || metrics.status !== 'ready') {
			return null;
		}
		const count = metrics.uniqueAddressCount;
		return isNumber(count) && count >= 0 ? count : null;
	}
}

function filterChipLabel(label: string): string {
	return label.replace(/\s+\|\s+[\d,]+\s+parcels?$/i, '');
}

function filterChipScopeCanFlyTo(filter: IFilter, icon: string): boolean {
	if (icon === 'person') {
		// Owner-shaped filters opt out — owners are people, not places,
		// so a fly-to motion has no defined geometry to land on.
		return false;
	}
	if (filter.geometry) {
		// Boundary filters (place / drawn area) carry a MultiPolygon
		// with a bbox; fly-to lands directly on it.
		return true;
	}
	// Phase 3 follow-up: address-search filters never carry a
	// `filter.geometry` (they're parcel-uid lists, not bounded
	// regions). When such a filter resolves to exactly one parcel, we
	// CAN fly to that parcel — resolve the bbox via the parcel
	// endpoint. The detection is structural (single include-child,
	// zero excludes) and runs entirely client-side from the IFilter
	// tree already loaded by the chip strip.
	if (singleParcelUidFromAddressFilter(filter)) {
		return true;
	}
	return false;
}

function filterChipScopeName(filter: IFilter): string {
	const icon = (filter.icon || '').trim().toLowerCase();
	if (filterChipLooksLikeOwner(filter) || ['person', 'person_search', 'owner', 'group'].indexOf(icon) >= 0) {
		return 'person';
	}
	if (['place', 'room', 'pin', 'location_on'].indexOf(icon) >= 0) {
		return 'place';
	}
	if (filter.geometry) {
		return 'draw_area';
	}
	return icon || 'place';
}

function filterChipLooksLikeOwner(filter: IFilter): boolean {
	const expression = filter.expression || null;
	return Boolean(expression && expression.lhs === 'parcel__owner_search_selection');
}

// Phase 3 follow-up to PR #73: detects address-search filters whose
// child tree resolves to a single parcel — exactly one include-child
// (`expression.lhs = 'parcel__address_search_uid'`, operator `exact`),
// zero excludes (operator `ne`). Mirrors the backend predicate at
// `Project._is_address_search_filter` / `_address_search_filter_q` in
// `listblox/projects/models.py`. Exported so `filtermanager.flyToFilter`
// can route these through a parcel-bbox lookup since address filters
// never carry `filter.geometry` directly.
export function singleParcelUidFromAddressFilter(filter: IFilter): string | null {
	const expression = filter.expression || null;
	if (!expression || expression.lhs !== 'parcel__address_search_selection') {
		return null;
	}
	const includes: Array<string> = [];
	const excludes: Array<string> = [];
	const children = filter.children || [];
	for (const child of children) {
		if (!child || !child.enabled) {
			continue;
		}
		const childExpr = child.expression;
		if (!childExpr || childExpr.lhs !== 'parcel__address_search_uid') {
			continue;
		}
		const operator = (childExpr.operator || '').trim();
		const rhs = (childExpr.rhs || '').trim();
		if (!rhs) {
			continue;
		}
		if (operator === 'exact') {
			includes.push(rhs);
		} else if (operator === 'ne') {
			excludes.push(rhs);
		}
	}
	// Single include, no excludes, no duplicate child UIDs — the
	// filter unambiguously targets one parcel.
	if (includes.length !== 1 || excludes.length !== 0) {
		return null;
	}
	return includes[0];
}

@OBJ
export class FilterEnabledToggle extends ToolButton {
	private checkedState: boolean;

	static StateIconName = {
		off: 'toggle_off',
		on: 'toggle_on',
	};

	static iconNameForState(enabled: boolean): string {
		return enabled ?
			this.StateIconName.on :
			this.StateIconName.off;
	}

	constructor(enabled: boolean, opts?: Partial<ToolButtonOpts>) {
		opts = opts || {};
		const classNames = opts.classNames ?
			(typeof opts.classNames === 'string' ? [opts.classNames] : stringIterableToStringArray(opts.classNames)) :
			[];
		opts.classNames = [
			'chip__toggle',
			...classNames,
		];
		super(opts);
		this.checkedState = false;
		new ElObj({
			attributes: [['aria-hidden', 'true']],
			classNames: 'chip__toggle-knob',
			parent: this,
			tagName: 'span',
		});
		this.setAttribute('role', 'switch');
		this.setCheckable(true);
		this.setCheckState(enabled ? CheckState.Checked : CheckState.Unchecked);
	}

	checkState(): CheckState {
		return this.checkedState ?
			CheckState.Checked :
			CheckState.Unchecked;
	}

	protected domMouseClickEvent(event: MouseEvent): void {
		event.stopImmediatePropagation();
		event.stopPropagation();
		super.domMouseClickEvent(event);
	}

	setCheckState(state: CheckState): void {
		const checked = state === CheckState.Checked;
		this.checkedState = checked;
		this.removeText();
		this.removeStyleProperty('color');
		this.setAttribute('aria-checked', checked ? 'true' : 'false');
		this.setTitle(checked ? 'Click to disable' : 'Click to enable');
	}

	setDisabled(disabled: boolean): void {
		super.setDisabled(disabled);
		if (disabled) {
			this.setStyleProperty('opacity', '0.38');
		} else {
			this.removeStyleProperty('opacity');
		}
	}
}

function filterChipScopeIcon(icon: string): ElObj {
	const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
	svg.setAttribute('viewBox', '0 0 24 24');
	svg.setAttribute('fill', 'none');
	svg.setAttribute('stroke', 'currentColor');
	svg.setAttribute('stroke-width', '2');
	svg.setAttribute('stroke-linecap', 'round');
	svg.setAttribute('stroke-linejoin', 'round');

	const add = (tagName: 'circle' | 'path', attrs: Array<[string, string]>): void => {
		const child = document.createElementNS('http://www.w3.org/2000/svg', tagName);
		for (const [name, value] of attrs) {
			child.setAttribute(name, value);
		}
		svg.appendChild(child);
	};

	switch (icon) {
		case 'person':
		case 'person_search':
		case 'owner':
			add('path', [['d', 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2']]);
			add('circle', [['cx', '12'], ['cy', '7'], ['r', '4']]);
			break;
		case 'select_all':
		case 'polyline':
		case 'shape':
		case 'draw_area':
		case 'draw':
			add('path', [['d', 'M6 4l12 3v10l-8 3-5-5z']]);
			add('circle', [['cx', '6'], ['cy', '4'], ['r', '1.6']]);
			add('circle', [['cx', '18'], ['cy', '7'], ['r', '1.6']]);
			add('circle', [['cx', '18'], ['cy', '17'], ['r', '1.6']]);
			add('circle', [['cx', '10'], ['cy', '20'], ['r', '1.6']]);
			add('circle', [['cx', '5'], ['cy', '15'], ['r', '1.6']]);
			break;
		case 'select_all_legacy':
			add('path', [['d', 'M4 4h7v7H4z']]);
			add('path', [['d', 'M13 13h7v7h-7z']]);
			add('path', [['d', 'M11 11l2 2']]);
			break;
		case 'place':
		default:
			add('path', [['d', 'M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 1 1 18 0z']]);
			add('circle', [['cx', '12'], ['cy', '10'], ['r', '3']]);
			break;
	}

	return new ElObj(svg);
}
